/**
 * MEDICATION SERVICE V2
 * 
 * Service para gerenciar medicações usando as tabelas v2
 * - medications_v2
 * - medication_logs_v2
 * 
 * Data: 15/10/2025
 */

import { supabase } from '../lib/supabase';
import { runOnlineOrToast } from '../utils/networkGuard';
import { getTodayBRT } from '../utils/dateUtils';
import { isMedicationInPauseOn, isMedicationFinishedByYmd } from '../utils/pauseUtils';
import type {
  Medication_v2,
  Medication_v2_Insert,
  Medication_v2_Update,
  MedicationLog_v2,
  RegisterDoseResponse,
  DoseStatus
} from '../types/database-v2.types';

// ============================================
// MEDICATIONS
// ============================================

/**
 * Busca todas as medicações de um membro
 */
export async function getMemberMedications(memberId: string): Promise<Medication_v2[]> {
  const { data, error } = await supabase
    .from('medications_v2' as any)
    .select(`
      id,
      member_id,
      remedy_name,
      potency,
      dosage_type,
      quantity,
      frequency,
      times,
      start_date,
      end_date,
      usage_days,
      pause_days,
      total_period_months,
      notes,
      is_active,
      created_at,
      updated_at,
      doses_expected
    `)
    .eq('member_id', memberId)
    .eq('is_active', true) // CORRIGIDO: is_active, não active
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Erro ao buscar medicações:', error);
    throw error;
  }

  return data || [];
}

/**
 * Retorna um mapa de aderência unificada por medicação do membro
 * { [medication_id]: adherence_percentage_unified }
 */
export async function getMemberMedicationUnifiedAdherence(
  memberId: string
): Promise<Record<string, number>> {
  const map: Record<string, number> = {};
  try {
    const { data, error } = await supabase
      .from('medication_logs_v2' as any)
      .select('medication_id, adherence_percentage_unified')
      .eq('member_id', memberId);

    if (error) {
      console.error('Erro ao buscar aderência unificada por medicação:', error);
      return map;
    }

    (data || []).forEach((row: any) => {
      const medId = row.medication_id as string;
      const perc = Number(row.adherence_percentage_unified) || 0;
      // Como o valor é igual entre os time_slots da mesma medicação,
      // podemos simplesmente atribuir a primeira ocorrência
      if (!(medId in map)) {
        map[medId] = perc;
      }
    });

    return map;
  } catch (err) {
    console.error('Erro inesperado ao buscar aderência unificada:', err);
    return map;
  }
}

/**
 * Busca uma medicação específica
 */
export async function getMedication(medicationId: string): Promise<Medication_v2 | null> {
  const { data, error } = await supabase
    .from('medications_v2')
    .select(`
      id,
      member_id,
      remedy_name,
      potency,
      dosage_type,
      quantity,
      frequency,
      times,
      start_date,
      end_date,
      usage_days,
      pause_days,
      total_period_months,
      notes,
      is_active,
      created_at,
      updated_at,
      doses_expected
    `)
    .eq('id', medicationId)
    .single();

  if (error) {
    console.error('Erro ao buscar medicação:', error);
    throw error;
  }

  return data;
}

/**
 * Cria uma nova medicação
 */
export async function createMedication(medication: Medication_v2_Insert): Promise<Medication_v2> {
  const { data, error } = await runOnlineOrToast(async () =>
    supabase
      .from('medications_v2')
      .insert(medication)
      .select('id, member_id, updated_at')
      .single()
  );

  if (error) {
    console.error('Erro ao criar medicação:', error);
    throw error;
  }

  return data as Medication_v2;
}

/**
 * Atualiza uma medicação
 */
export async function updateMedication(
  medicationId: string,
  updates: Medication_v2_Update
): Promise<Medication_v2> {
  const { data, error } = await runOnlineOrToast(async () =>
    supabase
      .from('medications_v2')
      .update(updates)
      .eq('id', medicationId)
      .select('id, member_id, updated_at')
      .single()
  );

  if (error) {
    console.error('Erro ao atualizar medicação:', error);
    throw error;
  }

  return data as Medication_v2;
}

/**
 * Deleta uma medicação (soft delete - marca como inativa)
 */
export async function deleteMedication(medicationId: string): Promise<void> {
  const { error } = await runOnlineOrToast(async () =>
    supabase
      .from('medications_v2' as any)
      .update({ is_active: false }) // CORRIGIDO: is_active, não active
      .eq('id', medicationId)
  );

  if (error) {
    console.error('Erro ao deletar medicação:', error);
    throw error;
  }
}

// ============================================
// MEDICATION LOGS
// ============================================

/**
 * Busca os logs de medicação de um membro
 */
export async function getMemberMedicationLogs(memberId: string): Promise<MedicationLog_v2[]> {
  const { data, error } = await supabase
    .from('medication_logs_v2')
    .select(`
      id,
      medication_id,
      member_id,
      start_date,
      time_slot,
      doses_expected,
      taken,
      taken_late,
      missed,
      pending,
      adherence_percentage_slot,
      adherence_percentage_unified,
      points_earned,
      doses_detail,
      created_at,
      updated_at
    `)
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Erro ao buscar logs de medicação:', error);
    throw error;
  }

  return data || [];
}

/**
 * Busca os logs de uma medicação específica
 */
export async function getMedicationLogs(medicationId: string): Promise<MedicationLog_v2[]> {
  const { data, error } = await supabase
    .from('medication_logs_v2')
    .select(`
      id,
      medication_id,
      member_id,
      start_date,
      time_slot,
      doses_expected,
      taken,
      taken_late,
      missed,
      pending,
      adherence_percentage_slot,
      adherence_percentage_unified,
      points_earned,
      doses_detail,
      created_at,
      updated_at
    `)
    .eq('medication_id', medicationId)
    .order('time_slot', { ascending: true });

  if (error) {
    console.error('Erro ao buscar logs:', error);
    throw error;
  }

  return data || [];
}

// ============================================
// REGISTER DOSE (Função PostgreSQL)
// ============================================

/**
 * Registra uma dose usando a função PostgreSQL register_dose_v2
 * 
 * @param medicationId - ID da medicação
 * @param timeSlot - Horário da dose (ex: '09:00')
 * @param takenAt - Timestamp de quando foi tomada (opcional, padrão: NOW())
 * @returns Resposta da função com status e pontos
 */
export async function registerDose(
  medicationId: string,
  timeSlot: string,
  takenAt?: string
): Promise<RegisterDoseResponse> {
  try {
    const { data, error } = await runOnlineOrToast(async () =>
      supabase.rpc('register_dose_v2', {
        p_medication_id: medicationId,
        p_time_slot: timeSlot,
        p_taken_at: takenAt || new Date().toISOString()
      })
    );

    if (error) {
      console.error('Erro ao registrar dose:', error);
      return {
        success: false,
        error: error.message
      };
    }

    return data as RegisterDoseResponse;
  } catch (err) {
    console.error('Erro ao registrar dose:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Erro desconhecido'
    };
  }
}

// ============================================
// HELPERS
// ============================================

/**
 * Verifica se uma dose já foi tomada hoje
 */
export function isDoseTakenToday(log: MedicationLog_v2, _timeSlot: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  
  const todayDoses = log.doses_detail.filter(dose => {
    const doseDate = dose.scheduled_time.split('T')[0];
    return doseDate === today && dose.status !== 'pending';
  });

  return todayDoses.length > 0;
}

/**
 * Retorna o status da dose para um horário específico hoje
 */
export function getTodayDoseStatus(log: MedicationLog_v2, timeSlot: string): DoseStatus | null {
  const today = new Date().toISOString().split('T')[0];
  
  const todayDose = log.doses_detail.find(dose => {
    const doseDate = dose.scheduled_time.split('T')[0];
    const doseTime = dose.scheduled_time.split('T')[1]?.substring(0, 5);
    return doseDate === today && doseTime === timeSlot;
  });

  return todayDose?.status || null;
}

/**
 * Calcula o total de doses tomadas hoje
 */
export function getTodayDosesTaken(logs: MedicationLog_v2[]): number {
  const today = new Date().toISOString().split('T')[0];
  
  let total = 0;
  logs.forEach(log => {
    const todayDoses = log.doses_detail.filter(dose => {
      const doseDate = dose.scheduled_time.split('T')[0];
      return doseDate === today && (dose.status === 'taken' || dose.status === 'taken_late');
    });
    total += todayDoses.length;
  });

  return total;
}

/**
 * Calcula o total de doses esperadas hoje
 */
export function getTodayDosesExpected(medications: Medication_v2[]): number {
  const today = getTodayBRT();
  return medications.reduce((total, med) => {
    // Ignorar tratamentos finalizados (end_date ou fim planejado) e pausas de hoje
    if (isMedicationFinishedByYmd(med, today)) return total;
    if (isMedicationInPauseOn(med, today)) return total;
    return total + (med.times?.length || 0);
  }, 0);
}

/**
 * Calcula a porcentagem de doses tomadas hoje
 */
export function getTodayAdherencePercentage(
  medications: Medication_v2[],
  logs: MedicationLog_v2[]
): number {
  const expected = getTodayDosesExpected(medications);
  if (expected === 0) return 100;
  
  const taken = getTodayDosesTaken(logs);
  return Math.round((taken / expected) * 100);
}

/**
 * Retorna as próximas doses do dia
 */
export function getUpcomingDoses(
  medications: Medication_v2[],
  logs: MedicationLog_v2[]
): Array<{
  medicationId: string;
  medicationName: string;
  timeSlot: string;
  status: DoseStatus | null;
}> {
  const now = new Date();
  const currentTime = now.toTimeString().substring(0, 5);

  const doses: Array<{
    medicationId: string;
    medicationName: string;
    timeSlot: string;
    status: DoseStatus | null;
  }> = [];

  medications.forEach(med => {
    med.times.forEach(time => {
      if (time >= currentTime) {
        const log = logs.find(l => l.medication_id === med.id && l.time_slot === time);
        const status = log ? getTodayDoseStatus(log, time) : null;
        
        doses.push({
          medicationId: med.id,
          medicationName: med.remedy_name,
          timeSlot: time,
          status
        });
      }
    });
  });

  return doses.sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));
}

// ============================================
// EXPORT
// ============================================

const MedicationService = {
  // Medications
  getMemberMedications,
  getMedication,
  createMedication,
  updateMedication,
  deleteMedication,
  
  // Logs
  getMemberMedicationLogs,
  getMedicationLogs,
  getMemberMedicationUnifiedAdherence,
  
  // Register Dose
  registerDose,
  
  // Helpers
  isDoseTakenToday,
  getTodayDoseStatus,
  getTodayDosesTaken,
  getTodayDosesExpected,
  getTodayAdherencePercentage,
  getUpcomingDoses
};

export default MedicationService;
